import {
  Alert,
  Badge,
  Button,
  Code,
  FileInput,
  Group,
  Paper,
  PasswordInput,
  Progress,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconDownload, IconFileTypeCsv, IconUpload } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useClockifyWorkspaces,
  useCsvPreview,
  useImportJobs,
  useRunCsvImport,
  useStartClockifyImport,
  type CsvPreview,
  type ImportJob,
} from '@/api/imports';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { useApiError } from '@/hooks/useApiError';
import dayjs from '@/lib/dayjs';
import { dateTimeFormat } from '@/lib/format';

const CSV_FIELDS = [
  'DESCRIPTION', 'PROJECT', 'CLIENT', 'TASK', 'TAGS', 'USER_EMAIL', 'USER_NAME', 'BILLABLE',
  'START_DATE', 'START_TIME', 'END_DATE', 'END_TIME', 'DURATION', 'START_DATETIME', 'END_DATETIME',
] as const;

/** Datenübernahme: Clockify-Import (MIG-01 – MIG-05), CSV-Import (MIG-06) und Workspace-Export (MIG-07). */
export function ImportExportSettings() {
  return (
    <Stack>
      <ClockifyImport />
      <CsvImport />
      <ImportJobs />
      <WorkspaceExport />
    </Stack>
  );
}

function ClockifyImport() {
  const { t } = useTranslation();
  const { workspaceId } = useCurrentWorkspace();
  const onError = useApiError();
  const workspaces = useClockifyWorkspaces(workspaceId);
  const start = useStartClockifyImport(workspaceId);
  const [apiKey, setApiKey] = useState('');
  const [source, setSource] = useState<string | null>(null);
  const [range, setRange] = useState<[string | null, string | null]>([dayjs().subtract(1, 'year').format('YYYY-MM-DD'), dayjs().format('YYYY-MM-DD')]);
  const [importRates, setImportRates] = useState(true);
  const [importGroups, setImportGroups] = useState(true);
  const [importTimeOff, setImportTimeOff] = useState(true);
  const [importInvoices, setImportInvoices] = useState(true);
  const [importExpenses, setImportExpenses] = useState(true);
  const [importSchedule, setImportSchedule] = useState(true);

  return (
    <Paper withBorder p="md">
      <Title order={5} mb="xs">
        {t('import.clockifyTitle')}
      </Title>
      <Text size="sm" c="dimmed" mb="sm">
        {t('import.clockifyText')}
      </Text>
      <Stack maw={560}>
        <Group align="flex-end">
          <PasswordInput label={t('import.apiKey')} value={apiKey} onChange={(e) => setApiKey(e.currentTarget.value)} style={{ flex: 1 }} />
          <Button variant="default" disabled={!apiKey} loading={workspaces.isPending} onClick={() => workspaces.mutate(apiKey, { onError })}>
            {t('import.connect')}
          </Button>
        </Group>
        {workspaces.data && (
          <>
            <Select
              label={t('import.sourceWorkspace')}
              data={workspaces.data.map((w) => ({ value: w.id, label: w.name }))}
              value={source}
              onChange={setSource}
            />
            <DatePickerInput type="range" label={t('import.entriesRange')} value={range} onChange={(v) => setRange(v as [string | null, string | null])} />
            <Switch label={t('import.importRates')} checked={importRates} onChange={(e) => setImportRates(e.currentTarget.checked)} />
            <Switch label={t('import.importGroups')} checked={importGroups} onChange={(e) => setImportGroups(e.currentTarget.checked)} />
            <Switch label={t('import.importTimeOff')} checked={importTimeOff} onChange={(e) => setImportTimeOff(e.currentTarget.checked)} />
            <Switch label={t('import.importInvoices')} checked={importInvoices} onChange={(e) => setImportInvoices(e.currentTarget.checked)} />
            <Switch label={t('import.importExpenses')} checked={importExpenses} onChange={(e) => setImportExpenses(e.currentTarget.checked)} />
            <Switch label={t('import.importSchedule')} checked={importSchedule} onChange={(e) => setImportSchedule(e.currentTarget.checked)} />
            <Alert color="blue">{t('import.clockifyHint')}</Alert>
            <Group>
              <Button
                leftSection={<IconUpload size={16} />}
                disabled={!source}
                loading={start.isPending}
                onClick={() =>
                  start.mutate(
                    {
                      apiKey,
                      options: {
                        sourceWorkspaceId: source!,
                        entriesFrom: range[0] ?? undefined,
                        entriesTo: range[1] ?? undefined,
                        importRates,
                        importGroups,
                        importTimeOff,
                        importInvoices,
                        importExpenses,
                        importSchedule,
                      },
                    },
                    { onSuccess: () => setApiKey(''), onError },
                  )
                }
              >
                {t('import.start')}
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Paper>
  );
}

function CsvImport() {
  const { t } = useTranslation();
  const { workspaceId, prefs } = useCurrentWorkspace();
  const onError = useApiError();
  const preview = useCsvPreview(workspaceId);
  const run = useRunCsvImport(workspaceId);
  const [file, setFile] = useState<File | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [result, setResult] = useState<ImportJob | null>(null);

  const load = (f: File | null) => {
    setFile(f);
    setResult(null);
    if (f)
      preview.mutate(f, {
        onSuccess: (p: CsvPreview) => setMapping({ ...(p.mapping as Record<string, string>) }),
        onError,
      });
  };
  const headers = preview.data?.headers ?? [];

  return (
    <Paper withBorder p="md">
      <Title order={5} mb="xs">
        {t('import.csvTitle')}
      </Title>
      <Text size="sm" c="dimmed" mb="sm">
        {t('import.csvText')}
      </Text>
      <Stack>
        <FileInput
          maw={400}
          accept=".csv,text/csv"
          leftSection={<IconFileTypeCsv size={16} />}
          placeholder={t('import.chooseFile')}
          value={file}
          onChange={load}
          clearable
        />
        {preview.data && file && (
          <>
            <Text size="sm">{t('import.rowsFound', { count: preview.data.totalRows ?? 0 })}</Text>
            <SimpleGrid cols={{ base: 1, sm: 3 }}>
              {CSV_FIELDS.map((field) => (
                <Select
                  key={field}
                  size="xs"
                  label={t(`import.fields.${field}`)}
                  data={headers}
                  value={mapping[field] ?? null}
                  onChange={(v) =>
                    setMapping((m) => {
                      const next = { ...m };
                      if (v) next[field] = v;
                      else delete next[field];
                      return next;
                    })
                  }
                  clearable
                />
              ))}
            </SimpleGrid>
            <Table.ScrollContainer minWidth={600}>
              <Table fz="xs" withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    {headers.map((h) => (
                      <Table.Th key={h}>{h}</Table.Th>
                    ))}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(preview.data.rows ?? []).slice(0, 5).map((row, i) => (
                    <Table.Tr key={i}>
                      {row.map((c, j) => (
                        <Table.Td key={j}>{c}</Table.Td>
                      ))}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
            <Group>
              <Button
                leftSection={<IconUpload size={16} />}
                loading={run.isPending}
                onClick={() =>
                  run.mutate(
                    { file, options: { mapping, createMissing: true, timezone: prefs.timezone } },
                    { onSuccess: setResult, onError },
                  )
                }
              >
                {t('import.run')}
              </Button>
            </Group>
          </>
        )}
        {result && <JobSummary job={result} />}
      </Stack>
    </Paper>
  );
}

function JobSummary({ job }: { job: ImportJob }) {
  const { t } = useTranslation();
  return (
    <Alert color={job.status === 'FAILED' ? 'red' : 'green'} title={t(`import.status.${job.status}`)}>
      <Code block>{job.log.trim().split('\n').slice(-12).join('\n')}</Code>
    </Alert>
  );
}

function ImportJobs() {
  const { t } = useTranslation();
  const { workspaceId, prefs } = useCurrentWorkspace();
  const { data: jobs = [] } = useImportJobs(workspaceId);
  const [open, setOpen] = useState<string | null>(null);
  if (jobs.length === 0) return null;
  const color = { QUEUED: 'gray', RUNNING: 'blue', DONE: 'green', FAILED: 'red', CANCELED: 'gray' } as const;
  return (
    <Paper withBorder p="md">
      <Title order={5} mb="sm">
        {t('import.history')}
      </Title>
      <Table highlightOnHover>
        <Table.Tbody>
          {jobs.map((job) => (
            <Table.Tr key={job.id} onClick={() => setOpen(open === job.id ? null : job.id)} style={{ cursor: 'pointer' }}>
              <Table.Td>
                <Text size="sm" fw={500}>
                  {job.name}
                </Text>
                <Text size="xs" c="dimmed">
                  {dayjs(job.createdAt).tz(prefs.timezone).format(dateTimeFormat(prefs))}
                </Text>
                {open === job.id && (
                  <Code block mt="xs">
                    {job.log}
                  </Code>
                )}
              </Table.Td>
              <Table.Td w={220}>
                {job.status === 'RUNNING' ? <Progress value={100} animated striped /> : null}
                <Text size="xs" c="dimmed">
                  {Object.entries(job.progress)
                    .map(([k, v]) => `${t(`import.counts.${k}`, { defaultValue: k })}: ${v}`)
                    .join(' · ')}
                </Text>
              </Table.Td>
              <Table.Td w={120} ta="right">
                <Badge color={color[job.status]} variant="light">
                  {t(`import.status.${job.status}`)}
                </Badge>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Paper>
  );
}

function WorkspaceExport() {
  const { t } = useTranslation();
  const { workspaceId } = useCurrentWorkspace();
  return (
    <Paper withBorder p="md">
      <Title order={5} mb="xs">
        {t('import.exportTitle')}
      </Title>
      <Text size="sm" c="dimmed" mb="sm">
        {t('import.exportText')}
      </Text>
      <Button component="a" href={`/api/v1/workspaces/${workspaceId}/export`} variant="default" leftSection={<IconDownload size={16} />}>
        {t('import.exportButton')}
      </Button>
    </Paper>
  );
}
